import { useEffect, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'

import type { ChannelType } from '@buffer-mono/popcorn'

import { ReactionIcon } from './ReactionIcon'
import {
  useCommentData,
  useCommentReactionError,
} from '../../Comments/context/CommentContext'
import {
  CommentDeletedError,
  useReactToComment,
} from '../../hooks/comments/useReactToComment'
import { FacebookReactionsHoverCard } from '../FacebookReactionsHoverCard'
import {
  FirstTimeLikeCoachmark,
  useFirstTimeLikeCoachmark,
} from '../FirstTimeLikeCoachmark'
import { VIEW_ONLY_COMMENT_TOOLTIP } from '../../shared/viewOnlyCopy'

import styles from './APIReaction.module.css'

type SingleReactionProps = {
  likingIcon?: string | null
  /** Source network, used by the first-time like coachmark illustration. */
  service?: ChannelType
  /** Display name of the source network, used in the tooltip's sync caveat. */
  networkName?: string
  targetLabel?: string
  /**
   * When true, the like state is shown but the button is non-interactive.
   * Used for view-only Community actors so the existing reaction state is
   * visible without granting the ability to toggle it.
   */
  disabled?: boolean
}

export const SingleReaction = ({
  likingIcon,
  service,
  networkName,
  targetLabel,
  disabled = false,
}: SingleReactionProps): JSX.Element => {
  const comment = useCommentData()
  const { reactToComment, loading } = useReactToComment()
  const { setReactionError } = useCommentReactionError()

  const isLikedFromContext = (comment?.reactions?.length ?? 0) > 0
  const isMutatingRef = useRef(false)

  const [optimisticIsReacted, setOptimisticIsReacted] =
    useState(isLikedFromContext)

  const coachmark = useFirstTimeLikeCoachmark(service ?? '')

  useEffect(() => {
    if (!isMutatingRef.current) {
      setOptimisticIsReacted(isLikedFromContext)
    }
  }, [isLikedFromContext])

  const handleClick = async (e: React.MouseEvent): Promise<void> => {
    if (isMutatingRef.current) return
    e.stopPropagation()
    setReactionError(null)

    const newLikedState = !optimisticIsReacted
    isMutatingRef.current = true
    setOptimisticIsReacted(newLikedState)

    // Explain one-way sync the first time the actor likes on this network.
    // Fire optimistically, in step with the optimistic like, so the coachmark
    // appears immediately instead of after the network round-trip confirms.
    if (newLikedState) {
      coachmark.trigger()
    }

    try {
      await reactToComment(comment.id, newLikedState)
    } catch (err) {
      setOptimisticIsReacted(!newLikedState)

      // The coachmark was opened ahead of the response, so take it back down
      // when the like it explained never landed. Closing without dismissing
      // keeps it available for the actor's next first like.
      if (newLikedState) {
        coachmark.close()
      }

      if (!(err instanceof CommentDeletedError)) {
        setReactionError(
          'An error occurred while reacting to the comment. Please try again later or contact support',
        )
      }
    } finally {
      isMutatingRef.current = false
    }
  }

  // On Facebook the reactions hover card is the hover affordance, so suppress
  // the icon's own tooltip there to avoid two overlays on the same icon.
  const showFacebookHoverCard = service === 'facebook' && !disabled

  const reactionIcon = (
    <ReactionIcon
      likingIcon={likingIcon}
      isLiked={optimisticIsReacted}
      networkName={networkName}
      targetLabel={targetLabel}
      loading={loading}
      showTooltip={!showFacebookHoverCard}
      disabled={disabled}
      tooltipOverride={disabled ? VIEW_ONLY_COMMENT_TOOLTIP : undefined}
      onClick={(e): void => {
        void handleClick(e)
      }}
    />
  )

  // Facebook only supports a like via Buffer; explain on hover that other
  // reactions are done on Facebook. Suppressed while the first-time coachmark
  // owns the icon so the two overlays never show together.
  const anchoredIcon = showFacebookHoverCard ? (
    <FacebookReactionsHoverCard suppressed={coachmark.open}>
      {reactionIcon}
    </FacebookReactionsHoverCard>
  ) : (
    reactionIcon
  )

  return (
    <ReactionContainer>
      {service && networkName ? (
        <FirstTimeLikeCoachmark
          open={coachmark.open}
          onDismiss={coachmark.dismiss}
          service={service}
          networkName={networkName}
        >
          {anchoredIcon}
        </FirstTimeLikeCoachmark>
      ) : (
        anchoredIcon
      )}
    </ReactionContainer>
  )
}

export const ReactionContainer = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => (
  <div data-comment-reaction className={styles.container}>
    {children}
  </div>
)
